/**
 * Lift2Fit — coaching workspace: Summary (`#/crm`).
 *
 * The coach's home. Aggregates only: client totals, whose programme is due a
 * review, today's and this week's sessions, the revenue headline and the top
 * insight flags. Every figure comes from `GET /crm/summary` (plus one aggregate
 * insights call and one due-payments call for the overdue number) — a roster of
 * dozens never triggers a per-client read.
 */
import { api, type CrmPayment } from '@/lib/api';
import { formatCompactCount, formatCompactMoney, formatCount, formatDate } from '@/lib/format';
import { AlertIcon, ArrowRightIcon, CheckIcon, SparkIcon, TargetIcon, TimerIcon } from '@/lib/icons';
import {
  AppLinkButton,
  EmptyState,
  LoadingPanel,
  MetricTile,
  Panel,
  ScreenHeader,
  StatusChip,
} from '../components';
import { useAsync, useCachedAsync } from '../hooks';
import { Link } from '../router';
import {
  CRM_EYEBROW,
  ListMore,
  SUMMARY_LIST_LIMIT,
  crmFlagLabel,
  formatMoney,
  formatSlotRange,
  severityTone,
  slotKindLabel,
  startOfLocalDay,
} from './shared';

/** Due payments already past their due date — the one "overdue" number. */
function overdueTotal(payments: CrmPayment[], now: Date): number {
  const today = startOfLocalDay(now).getTime();
  return payments.reduce((sum, payment) => {
    if (!payment.dueOn) return sum;
    const due = new Date(payment.dueOn);
    if (Number.isNaN(due.getTime())) return sum;
    return startOfLocalDay(due).getTime() < today ? sum + payment.amount : sum;
  }, 0);
}

export function CrmSummaryScreen() {
  const summaryState = useCachedAsync('crm/summary', () => api.crmSummary(), []);
  const insightsState = useCachedAsync('crm/insights', () => api.crmInsights(), []);
  const dueState = useAsync(() => api.crmPayments({ status: 'due' }), []);

  const summary = summaryState.data;

  if (summaryState.loading && !summary) {
    return (
      <div className="flex flex-col gap-4">
        <LoadingPanel label="Loading your clients" rows={3} />
        <LoadingPanel label="Loading your week" rows={3} />
      </div>
    );
  }

  if (!summary) {
    return (
      <EmptyState
        title="We could not read your summary"
        body={summaryState.error ?? 'The API did not answer. Check that you are signed in as a coach.'}
        icon={<AlertIcon className="h-8 w-8 text-accent" />}
        action={
          <AppLinkButton to="/crm/calendar" variant="outline">
            Open the calendar
          </AppLinkButton>
        }
      />
    );
  }

  const byCode = Object.entries(insightsState.data?.totals.byCode ?? {}).sort((a, b) => b[1] - a[1]);
  const topFlags = byCode.slice(0, 4);
  const overdue = overdueTotal(dueState.data?.data ?? [], new Date());
  const currency = summary.revenue?.currency ?? 'GBP';
  // The panel keeps a predictable height; `reviews.due` is the honest total, so
  // a coach with a dozen blocks due sees how many are held back and where to go.
  const shownReviews = summary.reviews.clients.slice(0, SUMMARY_LIST_LIMIT);
  const shownFlags = topFlags.length;

  return (
    <div className="flex flex-col gap-6" data-testid="crm-summary">
      <ScreenHeader
        eyebrow={CRM_EYEBROW}
        title="Summary"
        badge={<StatusChip tone="accent">Coaching workspace</StatusChip>}
        lead="Your book of business at a glance: who is training, what is booked, what is due and where to look first."
        actions={
          <AppLinkButton to="/crm/calendar">
            <TimerIcon className="text-[14px]" />
            Open the calendar
          </AppLinkButton>
        }
      />

      {summary.clients.total === 0 ? (
        <EmptyState
          title="No clients yet"
          body="Invite a lifter from the coaching flow and their shared training lands here. You keep the fields they choose to share, and only while the link is live."
          icon={<SparkIcon className="h-8 w-8 text-accent" />}
          action={
            <AppLinkButton to="/coach" variant="outline">
              Invite a client
              <ArrowRightIcon />
            </AppLinkButton>
          }
        />
      ) : null}

      <section aria-label="Client totals" data-testid="crm-summary-clients" className="grid grid-cols-2 gap-3 lg:grid-cols-5">
        <MetricTile label="Clients" value={formatCompactCount(summary.clients.total)} valueTitle={formatCount(summary.clients.total)} hint="active links" icon={<TargetIcon />} />
        <MetricTile
          label="Active"
          value={formatCompactCount(summary.clients.active)}
          valueTitle={formatCount(summary.clients.active)}
          hint="trained in the last 14 days"
          hintTone="success"
        />
        <MetricTile
          label="Inactive"
          value={formatCompactCount(summary.clients.inactive)}
          valueTitle={formatCount(summary.clients.inactive)}
          hint="no session in 14 days"
          hintTone={summary.clients.inactive > 0 ? 'warning' : 'success'}
        />
        <MetricTile
          label="Missed"
          value={formatCompactCount(summary.clients.missed)}
          valueTitle={formatCount(summary.clients.missed)}
          hint="no-shows, last 28 days"
          hintTone={summary.clients.missed > 0 ? 'danger' : 'success'}
        />
        <MetricTile label="New" value={formatCompactCount(summary.clients.newLast28)} valueTitle={formatCount(summary.clients.newLast28)} hint="links made, last 28 days" />
      </section>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Panel
          title="This week"
          subtitle="Slots you have open, and the next few on the calendar."
          testId="crm-summary-sessions"
          actions={
            <Link
              to="/crm/calendar"
              className="inline-flex h-9 items-center gap-2 border border-hairline px-3 text-[11px] font-bold uppercase tracking-[0.08em] text-ink hover:border-accent hover:text-accent"
            >
              Calendar
              <ArrowRightIcon />
            </Link>
          }
        >
          <dl className="grid grid-cols-2 gap-3">
            <MetricTile label="Today" value={formatCompactCount(summary.sessions.today)} valueTitle={formatCount(summary.sessions.today)} hint="sessions scheduled" />
            <MetricTile label="This week" value={formatCompactCount(summary.sessions.thisWeek)} valueTitle={formatCount(summary.sessions.thisWeek)} hint="Monday to Sunday" />
            <MetricTile
              label="Attended"
              value={formatCompactCount(summary.sessions.attendedLast28)}
              valueTitle={formatCount(summary.sessions.attendedLast28)}
              hint="last 28 days"
              hintTone="success"
            />
            <MetricTile
              label="No-shows"
              value={formatCompactCount(summary.sessions.missedLast28)}
              valueTitle={formatCount(summary.sessions.missedLast28)}
              hint="last 28 days"
              hintTone={summary.sessions.missedLast28 > 0 ? 'danger' : 'success'}
            />
          </dl>
          {summary.sessions.next.length ? (
            <ul className="flex flex-col divide-y divide-hairline">
              {summary.sessions.next.map((slot) => (
                <li key={slot.slotId} className="flex flex-wrap items-center justify-between gap-2 py-2">
                  <span className="flex min-w-0 flex-col">
                    <span className="text-[13px] font-bold text-ink">{formatDate(slot.startsAt)}</span>
                    <span className="text-[12px] text-ink-muted">
                      {formatSlotRange(slot.startsAt, slot.endsAt)}
                      {slot.clientNames.length ? ` · ${slot.clientNames.join(', ')}` : ''}
                    </span>
                  </span>
                  <span className="flex items-center gap-2">
                    <StatusChip tone={slot.kind === 'group' ? 'accent' : 'neutral'}>
                      {slotKindLabel(slot.kind)}
                    </StatusChip>
                    <span className="l2f-tabular font-mono text-[13px] font-bold text-ink">
                      {slot.booked}/{slot.capacity}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-[13px] text-ink-muted">No sessions scheduled yet — create availability on the calendar.</p>
          )}
          <ListMore
            total={summary.sessions.nextTotal}
            shown={summary.sessions.next.length}
            to="/crm/calendar"
            toLabel="View all"
            testId="crm-summary-sessions-more"
          />
        </Panel>

        <Panel
          title="Programmes due for review"
          subtitle={`${summary.reviews.due} client(s) whose block is in its final week, or already past its end.`}
          testId="crm-summary-reviews"
        >
          {shownReviews.length ? (
            <ul className="flex flex-col divide-y divide-hairline">
              {shownReviews.map((review) => (
                <li key={review.clientId} data-testid={`crm-review-${review.clientId}`}>
                  <Link
                    to={`/crm/clients/${review.clientId}`}
                    className="flex flex-wrap items-center justify-between gap-2 py-3 hover:text-accent"
                  >
                    <span className="flex min-w-0 flex-col">
                      <span className="text-[14px] font-bold text-ink">{review.displayName}</span>
                      <span className="text-[12px] text-ink-muted">{review.reason}</span>
                    </span>
                    <span className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.08em] text-accent">
                      Open client
                      <ArrowRightIcon />
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState
              title="Every programme is current"
              body="No client's block is in its final week. The next one appears here as its block end approaches."
              icon={<CheckIcon className="h-8 w-8 text-success" />}
            />
          )}
          <ListMore
            total={summary.reviews.due}
            shown={shownReviews.length}
            to="/crm/todo"
            toLabel="View all"
            testId="crm-summary-reviews-more"
          />
        </Panel>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* 0030: an internal coach with no own book gets NO money panel — the
            API returns a null revenue block and `canSeeMoney: false`. */}
        {summary.canSeeMoney && summary.revenue ? (
          <Panel
            title="Revenue"
            subtitle="The ledger headline: paid, due and anything past its due date."
            testId="crm-summary-revenue"
          >
            {/* Two columns, not four: the Revenue panel shares its row with the
                flags panel, so four tiles would each be too narrow for an exact
                sub-£1,000 figure like `£120.00`. The compact form handles the
                growth; the layout gives the exact figure room. */}
            <dl className="grid grid-cols-2 gap-3">
              <MetricTile
                label="Paid"
                value={formatCompactMoney(summary.revenue.paidTotal, currency)}
                valueTitle={formatMoney(summary.revenue.paidTotal, currency)}
                hint={`${summary.revenue.paidCount} payment(s)`}
                hintTone="success"
                valueTestId="crm-summary-revenue-paid"
              />
              <MetricTile
                label="Due"
                value={formatCompactMoney(summary.revenue.dueTotal, currency)}
                valueTitle={formatMoney(summary.revenue.dueTotal, currency)}
                hint={`${summary.revenue.dueCount} outstanding`}
                hintTone={summary.revenue.dueTotal > 0 ? 'warning' : 'success'}
                valueTestId="crm-summary-revenue-due"
              />
              <MetricTile
                label="Overdue"
                value={formatCompactMoney(overdue, currency)}
                valueTitle={formatMoney(overdue, currency)}
                hint="due date has passed"
                hintTone={overdue > 0 ? 'danger' : 'success'}
                valueTestId="crm-summary-revenue-overdue"
              />
              <MetricTile
                label="Paid · 28d"
                value={formatCompactMoney(summary.revenue.paidLast28, currency)}
                valueTitle={formatMoney(summary.revenue.paidLast28, currency)}
                hint="last 28 days"
                valueTestId="crm-summary-revenue-paid28"
              />
            </dl>
            <Link
              to="/crm/payments"
              className="inline-flex w-fit items-center gap-2 text-[11px] font-bold uppercase tracking-[0.08em] text-accent hover:text-accent-hover"
            >
              Open the ledger
              <ArrowRightIcon />
            </Link>
          </Panel>
        ) : null}

        <Panel
          title="Top insight flags"
          subtitle="From the evaluation matrix and the roster aggregates — evidence, not a badge."
          testId="crm-summary-flags"
          actions={
            <Link
              to="/crm/insights"
              className="inline-flex h-9 items-center gap-2 border border-hairline px-3 text-[11px] font-bold uppercase tracking-[0.08em] text-ink hover:border-accent hover:text-accent"
            >
              Insights
              <ArrowRightIcon />
            </Link>
          }
        >
          {topFlags.length ? (
            <ul className="flex flex-col divide-y divide-hairline">
              {topFlags.map(([code, count]) => (
                <li key={code} className="flex items-center justify-between gap-4 py-2.5">
                  <span className="text-[14px] font-bold text-ink">{crmFlagLabel(code)}</span>
                  <span className="flex items-center gap-2">
                    <StatusChip tone={severityTone(count > 1 ? 'danger' : 'warning')}>
                      {count} client(s)
                    </StatusChip>
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState
              title="Nothing flagged right now"
              body="No client is stalling, dropping off or reporting pain in the current window."
              icon={<CheckIcon className="h-8 w-8 text-success" />}
            />
          )}
          {/* The panel shows the top four flag types; the count line says how
              many more there are, and the link below opens the full matrix. */}
          <ListMore total={byCode.length} shown={shownFlags} testId="crm-summary-flags-more" />
          <Link
            to="/crm/insights"
            className="inline-flex w-fit items-center gap-2 text-[11px] font-bold uppercase tracking-[0.08em] text-accent hover:text-accent-hover"
          >
            See why each client is flagged
            <ArrowRightIcon />
          </Link>
        </Panel>
      </div>

      <p className="text-[12px] text-ink-muted">
        Insight window {insightsState.data?.windowDays ?? 28} days · summary generated{' '}
        {formatDate(summary.generatedAt)}
      </p>
    </div>
  );
}
